import { useState } from 'react'
import type { PlanVersionSummary, StateWorkspaceSummary } from '@/lib/apiTypes'
import { usePlanPolicyResults, approvePlan } from '@/lib/hooks'
import { useToast } from '@/context/ToastContext'
import { SkeletonLine } from '@/shared/components/Skeleton'
import { PlanGateConfigEditor } from '@/features/plans/components/PlanGateConfigEditor'

// ── Policy tab (verdict + approve + gate config editor) ─────────────────────────
export function PlanPolicyTab({
  orgId, workspace, version, canAdmin, onApproved,
}: {
  orgId: string
  workspace: StateWorkspaceSummary
  version: PlanVersionSummary
  canAdmin: boolean
  onApproved: () => void
}) {
  const { showToast } = useToast()
  const blocked = workspace.blocked === 1
  const [reason, setReason] = useState('')
  const [approving, setApproving] = useState(false)

  // Real per-plan verdict + reasons (M4 gap fix): read the persisted plan_policy_results for THIS
  // version instead of inferring the verdict from the workspace `blocked` flag.
  const policy = usePlanPolicyResults(orgId, workspace.name, version.id)
  const results = policy.data?.results ?? []
  const builtin = results.find((r) => r.source === 'builtin') ?? null
  const failing = results.filter((r) => !r.passed)
  // This plan's own verdict: failed if any evaluated result failed; else passed once results exist.
  const planFailed = failing.length > 0
  const hasResults = results.length > 0

  async function handleApprove() {
    setApproving(true)
    try {
      const res = await approvePlan(orgId, workspace.name, version.id, reason.trim())
      if (res.cleared) showToast('Plan approved — the workspace block is cleared.', 'success')
      else showToast('Approval recorded, but this plan was not the one blocking the workspace.', 'warning')
      setReason('')
      onApproved()
      policy.refetch()
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setApproving(false)
    }
  }

  const destroys = version.destroy_count + version.replace_count
  const verdictColor = planFailed ? 'var(--red)' : 'var(--green)'

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* Real per-plan verdict + reasons */}
      {policy.loading ? (
        <SkeletonLine height={54} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderRadius: 6, background: 'var(--bg2)', border: `1px solid ${hasResults ? verdictColor : 'var(--border)'}` }}>
          <span style={{ color: hasResults ? verdictColor : 'var(--text3)', fontSize: '0.85rem' }}>{!hasResults ? '○' : planFailed ? '✗' : '✓'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.82rem', color: 'var(--text)' }}>
              {!hasResults
                ? 'No gate verdict recorded for this plan yet.'
                : planFailed
                  ? 'This plan failed the policy gate.'
                  : 'This plan passed the policy gate.'}
              {builtin && <span className="badge badge-neutral" style={{ marginLeft: 8, fontSize: '0.55rem' }}>{builtin.source}</span>}
            </div>
            {failing.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 16, fontSize: '0.72rem', color: 'var(--text2)' }}>
                {failing.map((r) => (
                  <li key={r.id}>
                    <span style={{ color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: '0.62rem' }}>{r.policy_name}</span>
                    {r.failure_msg ? ` — ${r.failure_msg}` : ' — failed'}
                  </li>
                ))}
              </ul>
            )}
            <div style={{ fontSize: '0.72rem', color: 'var(--text2)', marginTop: 4 }}>
              This plan: {destroys} destroy{destroys !== 1 ? 's' : ''} (incl. replace destroy-half){version.replace_count > 0 ? `, ${version.replace_count} replace` : ''}.
            </div>
          </div>
        </div>
      )}

      {/* Workspace next-apply block state (distinct from this plan's own verdict). */}
      {blocked && (
        <div style={{ fontSize: '0.72rem', color: 'var(--red)' }}>
          ⚠ The next apply for this workspace is blocked by the plan gate.
        </div>
      )}

      {/* Approve (admin only, when blocked) */}
      {blocked && canAdmin && (
        <div style={{ display: 'grid', gap: 8, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '12px 14px' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', color: 'var(--text3)', letterSpacing: '0.08em' }}>// approve this specific plan</div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional, ≤500 chars) — recorded on the one-shot override"
            maxLength={500}
            rows={2}
            style={{ padding: '6px 10px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: '0.72rem', resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" disabled={approving} onClick={handleApprove}>
              {approving ? <span className="spinner" /> : 'Approve plan'}
            </button>
            <span style={{ fontSize: '0.68rem', color: 'var(--text3)' }}>Clears the block only for this exact plan version. Two-person control: human admins only.</span>
          </div>
        </div>
      )}
      {blocked && !canAdmin && (
        <div style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>Only an org admin can approve a blocked plan.</div>
      )}

      {/* Gate config editor (admin only) */}
      {canAdmin && <PlanGateConfigEditor orgId={orgId} workspace={workspace} />}
    </div>
  )
}

